import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/layout/Layout";
import { Section } from "../components/common/Section";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
// Checkbox removed - using native HTML checkbox
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    inquiry_type: "",
    has_machine: false,
    requires_machine: false,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, inquiry_type: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.location || !formData.inquiry_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Your inquiry has been submitted successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        location: "",
        inquiry_type: "",
        has_machine: false,
        requires_machine: false,
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit your inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Contact Crown Amusement | Buy, Sell or Lease Skill Game Machines</title>
        <meta name="description" content="Contact Crown Amusement to buy, sell, or lease skill game cabinets, fish games, gaming kiosks, and gameboards. Get a quote for your venue. Serving Georgia and nationwide." />
        <link rel="canonical" href="https://crownamusement.com/contact" />
        <meta property="og:title" content="Contact Crown Amusement | Get a Quote for Gaming Machines" />
        <meta property="og:description" content="Buy, sell, or lease skill game cabinets, fish games, kiosks, and gameboards. Contact our team today." />
        <meta property="og:url" content="https://crownamusement.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal to-obsidian" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20">
              Get in Touch
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white">
              Contact <span className="text-gold-gradient">Us</span>
            </h1>
            <p className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto">
              Ready to elevate your gaming business? Reach out to us for inquiries 
              about buying, selling, or leasing premium skill gaming machines.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <Section background="default">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl text-white mb-8">
              Let&apos;s Start a Conversation
            </h2>
            <p className="text-gray-400 leading-relaxed mb-10">
              Whether you&apos;re looking to purchase new gaming machines, sell your existing 
              inventory, or explore leasing options, our team is here to help you find 
              the perfect solution for your business.
            </p>

            <div className="space-y-6">
              <a
                href="tel:+18482198830"
                className="flex items-center gap-4 p-4 rounded-xl bg-surface border border-white/5 hover:border-gold-500/30 transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center group-hover:bg-gold-500/20 transition-colors">
                  <FaPhone className="text-gold-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Call Us</p>
                  <p className="text-white font-medium">+1 848-219-8830</p>
                </div>
              </a>

              <a
                href="mailto:info@crownamusement.com"
                className="flex items-center gap-4 p-4 rounded-xl bg-surface border border-white/5 hover:border-gold-500/30 transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center group-hover:bg-gold-500/20 transition-colors">
                  <FaEnvelope className="text-gold-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Email Us</p>
                  <p className="text-white font-medium">info@crownamusement.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-surface border border-white/5">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center">
                  <FaMapMarkerAlt className="text-gold-500" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Location</p>
                  <p className="text-white font-medium">United States</p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-10 p-6 rounded-xl bg-gold-500/5 border border-gold-500/10">
              <h3 className="font-heading text-lg text-white mb-4">Business Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Monday - Friday</span>
                  <span className="text-white">9:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Saturday</span>
                  <span className="text-white">10:00 AM - 4:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sunday</span>
                  <span className="text-white">Closed</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="p-8 rounded-2xl bg-surface border border-white/5">
                <h3 className="font-heading text-2xl text-white mb-6">Send Us a Message</h3>

                {/* Name */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    data-testid="input-name"
                    className="bg-charcoal border-white/10 text-white placeholder:text-gray-500 focus:border-gold-500 focus:ring-gold-500/20"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    data-testid="input-email"
                    className="bg-charcoal border-white/10 text-white placeholder:text-gray-500 focus:border-gold-500 focus:ring-gold-500/20"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    data-testid="input-phone"
                    className="bg-charcoal border-white/10 text-white placeholder:text-gray-500 focus:border-gold-500 focus:ring-gold-500/20"
                  />
                </div>

                {/* Location */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="location" className="text-gray-300">Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, State"
                    data-testid="input-location"
                    className="bg-charcoal border-white/10 text-white placeholder:text-gray-500 focus:border-gold-500 focus:ring-gold-500/20"
                  />
                </div>

                {/* Inquiry Type */}
                <div className="space-y-2 mb-6">
                  <Label className="text-gray-300">Inquiry Type *</Label>
                  <Select value={formData.inquiry_type} onValueChange={handleSelectChange}>
                    <SelectTrigger 
                      className="bg-charcoal border-white/10 text-white focus:border-gold-500 focus:ring-gold-500/20 relative z-10"
                      data-testid="select-inquiry-type"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-charcoal border-white/10 z-50">
                      <SelectItem value="buy" className="text-white hover:bg-gold-500/10 focus:bg-gold-500/10 cursor-pointer">
                        Buy Gaming Machines
                      </SelectItem>
                      <SelectItem value="sell" className="text-white hover:bg-gold-500/10 focus:bg-gold-500/10 cursor-pointer">
                        Sell Gaming Machines
                      </SelectItem>
                      <SelectItem value="lease" className="text-white hover:bg-gold-500/10 focus:bg-gold-500/10 cursor-pointer">
                        Lease / Vend Gaming Machines
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Checkboxes */}
                <div className="flex flex-col sm:flex-row gap-6 mb-6">
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="requires_machine"
                        checked={formData.requires_machine}
                        onChange={(e) => handleCheckboxChange("requires_machine", e.target.checked)}
                        data-testid="checkbox-requires-machine"
                        className="sr-only peer"
                      />
                      <div className="w-5 h-5 border-2 border-white/20 rounded bg-charcoal peer-checked:bg-gold-500 peer-checked:border-gold-500 transition-colors flex items-center justify-center">
                        {formData.requires_machine && (
                          <svg className="w-3 h-3 text-obsidian" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                      I require machines
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="has_machine"
                        checked={formData.has_machine}
                        onChange={(e) => handleCheckboxChange("has_machine", e.target.checked)}
                        data-testid="checkbox-has-machine"
                        className="sr-only peer"
                      />
                      <div className="w-5 h-5 border-2 border-white/20 rounded bg-charcoal peer-checked:bg-gold-500 peer-checked:border-gold-500 transition-colors flex items-center justify-center">
                        {formData.has_machine && (
                          <svg className="w-3 h-3 text-obsidian" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                      I already have machines
                    </span>
                  </label>
                </div>

                {/* Message */}
                <div className="space-y-2 mb-6">
                  <Label htmlFor="message" className="text-gray-300">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your needs..."
                    rows={4}
                    data-testid="input-message"
                    className="bg-charcoal border-white/10 text-white placeholder:text-gray-500 focus:border-gold-500 focus:ring-gold-500/20 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="w-full py-6 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-obsidian font-bold rounded-full transition-all shadow-gold-glow disabled:opacity-50"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      Submit Inquiry
                      <FaArrowRight className="ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </Section>
    </Layout>
  );
};

export default Contact;
